import { useNavigate } from 'react-router-dom';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { MainLayout } from '@/components/layout/MainLayout';
import { InfiniteScrollList } from '@/components/ui/infinite-scroll-list';
import { StatusBadge } from '@/components/ui/status-badge';
import { customerApi } from '@/lib/pillarApi';
import type { MerchantCustomerRecordSearchListItem } from '@pillar-portal/api-types';
import { z } from 'zod';

function formatDateTime(value: string | undefined): string {
    if (value === undefined) {
        return '—';
    }
    return new Date(value).toLocaleString();
}

function formatPrimaryContact(customer: MerchantCustomerRecordSearchListItem): string {
    if (customer.primary_email !== undefined && customer.primary_email.length > 0) {
        return customer.primary_email;
    }
    if (customer.primary_phone !== undefined && customer.primary_phone.length > 0) {
        return customer.primary_phone;
    }
    return '—';
}

export function CustomersPageContent() {
    const navigate = useNavigate();

    return (
        <InfiniteScrollList<MerchantCustomerRecordSearchListItem, number, ReadonlyArray<string>>
            pageTitle="Customers"
            cardTitle="Customers"
            queryPlaceholder="Search customers..."
            queryKeyPrefix={['merchantCustomers']}
            pageSize={50}
            fetchPage={async (params) => {
                const response = await customerApi.searchCustomers({
                    ...(params.terms !== undefined ? { terms: [...params.terms] } : {}),
                    limit: params.limit,
                    cursor: params.cursor,
                });
                return {
                    items: response.merchant_customer_records,
                    pagination: response.pagination,
                };
            }}
            parseCursorParam={(pageParam: unknown) => {
                if (pageParam === null || pageParam === undefined) {
                    return undefined;
                }
                const parseResult = z.number().safeParse(pageParam);
                if (!parseResult.success) {
                    throw new Error('Expected numeric cursor');
                }
                return parseResult.data;
            }}
            stateCopy={{
                loadingLabel: 'Loading customers...',
                emptyLabel: 'No customers found.',
                endOfListLabel: 'End of customers.',
                genericErrorLabel: 'Failed to load customers.',
            }}
            renderItems={({ items }) => (
                <div className="space-y-2">
                    {items.map((customer) => (
                        <button
                            key={customer.merchant_customer_record_uuid}
                            type="button"
                            className="w-full rounded-lg border p-4 text-left transition-colors hover:bg-gray-50"
                            onClick={() => navigate(`/customers/${customer.merchant_customer_record_uuid}`)}
                        >
                            <div className="flex items-start justify-between gap-6">
                                <div className="min-w-0 space-y-1">
                                    <div className="flex items-center gap-2">
                                        <h3 className="font-semibold">{customer.name}</h3>
                                        <StatusBadge status={customer.status} />
                                    </div>
                                    <p className="text-sm text-muted-foreground">
                                        Contact: {formatPrimaryContact(customer)}
                                    </p>
                                    <p className="text-sm text-muted-foreground">
                                        Organization:{' '}
                                        {customer.customer_organization_name ?? '—'}
                                    </p>
                                </div>
                                <div className="space-y-1 text-right text-sm">
                                    <p className="text-muted-foreground">
                                        Last request:{' '}
                                        {customer.last_payment_request_reference_number ?? '—'}
                                    </p>
                                    <p className="text-muted-foreground">
                                        Last activity:{' '}
                                        {formatDateTime(customer.last_payment_activity_at)}
                                    </p>
                                </div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        />
    );
}

export default function CustomersPage() {
    return (
        <ProtectedRoute>
            <MainLayout>
                <CustomersPageContent />
            </MainLayout>
        </ProtectedRoute>
    );
}
